import { effect, inject, provideAppInitializer } from '@angular/core';
import {
  browserTracingIntegration,
  init,
  setContext,
  setTags,
  setUser,
  TraceService,
} from '@sentry/angular';
import { PlatformType } from '@data-access';
import { environment } from '@shared-app-config';
import { SessionStore } from '../session/session.store';
import { toSentryAccount } from '../_utils/sentry-account';

/**
 * Called from each app's `main.ts` before `bootstrapApplication`, so errors thrown while the app
 * boots are captured too. All cabinets report to one Sentry project, told apart by the `app` tag.
 * The local environment has no DSN, so on the dev servers the SDK stays uninitialized and patches
 * nothing (fetch, XHR, history).
 */
export function initErrorMonitoring(platform: PlatformType): void {
  const { dsn, environment: sentryEnvironment, tracesSampleRate } = environment.sentry;
  if (!dsn) return;

  init({
    dsn,
    environment: sentryEnvironment,
    release: `fr8bot-web@${environment.buildInfo.version}`,
    initialScope: { tags: { app: platform } },
    integrations: [
      browserTracingIntegration({
        // The icon registry fetches every SVG over XHR, dozens per screen, burying the API spans.
        // The URL arrives as requested, relative (`assets/icons/x.svg`), so no leading slash here.
        shouldCreateSpanForRequest: (url) => !url.includes('assets/icons/'),
      }),
    ],
    tracesSampleRate,
    // The API's CORS allows `sentry-trace`/`baggage`; other hosts never get them.
    tracePropagationTargets: [environment.apiUrl],
  });
}

/**
 * Injecting `TraceService` once starts its router subscription, which names navigation spans.
 * The effect keeps the Sentry user in step with the session profile, so a logout clears it.
 */
export const provideErrorMonitoring = () =>
  provideAppInitializer(() => {
    inject(TraceService);
    const profile = inject(SessionStore).profile;

    effect(() => {
      const account = toSentryAccount(profile());
      setUser(account.user);
      setTags(account.tags);
      setContext('account', account.context);
    });
  });
